import { AlertTriangle, Loader2 } from 'lucide-react'

import { DelayedComponent } from '@/ui/atoms/delayed-component/DelayedComponent'
import { useElementSize } from '@/ui/utils/useElementSize'

export interface GenericChartProps {
  height: number
  width: number
}

export type ChartConfigDefinition<C> = C extends React.ComponentType<infer P>
  ? P extends GenericChartProps
    ? {
        component: C
        props: Omit<P, keyof GenericChartProps>
        isPending: boolean
        isError: boolean
      }
    : never
  : never

declare const __CHART_TAB_OPAQUE_TYPE__: unique symbol

export interface ChartConfig {
  component: React.ComponentType<GenericChartProps>
  props: GenericChartProps
  readonly [__CHART_TAB_OPAQUE_TYPE__]: true
  isPending: boolean
  isError: boolean
}

export interface ChartResultWrapperProps extends ChartConfig {
  height: number
}

export function ChartResultWrapper({ height, component: Chart, isError, isPending, props }: ChartResultWrapperProps) {
  const [ref, { width }] = useElementSize()

  if (isPending) {
    return (
      <div className="flex items-center justify-center" style={{ height }} ref={ref}>
        <DelayedComponent>
          <Loader2 className="h-8 animate-spin text-neutral-200" data-chromatic="ignore" />
        </DelayedComponent>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center" style={{ height }} ref={ref}>
        <div className="typography-label-3 flex items-center rounded-full bg-secondary px-3 py-1 text-secondary/80">
          <AlertTriangle className="h-4" /> Failed to load chart data
        </div>
      </div>
    )
  }

  return (
    <div ref={ref} className="w-full">
      <Chart {...props} height={height} width={width} />
    </div>
  )
}

export function createChartConfig<C>(config: ChartConfigDefinition<C>): ChartConfig {
  return config as unknown as ChartConfig
}
